import { toast } from 'sonner';

export interface AsyncActionOptions {
  timeoutMs?: number;
  successMessage?: string;
  errorMessage?: string;
  onSuccess?: () => void;
  onError?: (error: any) => void;
}

/**
 * Unified AsyncActionHandler utility to wrap all critical form submission & Firestore write events.
 * Handles loading states, 10-second timeout protection, and latency / error feedback.
 */
export async function handleAsyncAction<T>(
  action: () => Promise<T>,
  setLoading: (loading: boolean) => void,
  options: AsyncActionOptions = {}
): Promise<T | undefined> {
  const {
    timeoutMs = 0,
    successMessage,
    errorMessage = 'Operation failed. Please try again.',
    onSuccess,
    onError,
  } = options;

  setLoading(true);
  const startTime = Date.now();

  let timeoutId: NodeJS.Timeout | null = null;
  let isCompleted = false;

  const timeoutPromise = timeoutMs > 0 ? new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => {
      if (!isCompleted) {
        reject(new Error('TIMEOUT_EXCEEDED'));
      }
    }, timeoutMs);
  }) : null;

  try {
    const result = timeoutPromise ? await Promise.race([action(), timeoutPromise]) : await action();
    isCompleted = true;
    if (timeoutId) clearTimeout(timeoutId);

    const duration = Date.now() - startTime;
    if (duration > 3000) {
      console.warn(`[Performance Warning] Operation took ${duration}ms, exceeding expected latency threshold.`);
    }

    if (successMessage) {
      toast.success(successMessage);
    }
    if (onSuccess) {
      onSuccess();
    }
    return result;
  } catch (error: any) {
    isCompleted = true;
    if (timeoutId) clearTimeout(timeoutId);

    if (error?.message === 'TIMEOUT_EXCEEDED') {
      toast.error('The operation took longer than expected and timed out. Please check your network connection.');
    } else if (error?.message?.includes('Quota exceeded') || error?.message?.includes('quota')) {
      toast.error('Firebase Quota Exceeded. Please upgrade your Firebase plan or check billing/quota limits.');
    } else {
      console.error('Async action error:', error);
      toast.error(error?.message ? `${errorMessage}: ${error.message}` : errorMessage);
    }

    if (onError) {
      onError(error);
    }
    return undefined;
  } finally {
    setLoading(false);
  }
}
